import React from 'react';



function useDataLoading(
	loadDataCallback: (offset: number, lastSynced: number) => Promise<{
		skip: boolean,
	}>,
	context: ReadonlyArray<any>,
	shouldPoll: boolean = true,
) {
	const loadDataDelay = 500;
	const syncInterval = 1_000;

	const isMounted = React.useRef(true);

	const lastSyncedRef = React.useRef(Date.now());
	const loadTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
	const synchronizationTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
	const offsetRef = React.useRef(0);
	const shouldPollRef = React.useRef(shouldPoll);

	const triggerLoadRef = React.useRef<(offset: number) => Promise<void>>();

	React.useEffect(
		() => {
			offsetRef.current = 0;
			lastSyncedRef.current = Date.now();

			triggerLoadRef.current?.(0);
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		context,
	);

	triggerLoadRef.current = React.useCallback(
		async (offset: number) => {
			if (!isMounted.current) {
				return;
			}

			if (synchronizationTimeoutRef.current) {
				clearTimeout(synchronizationTimeoutRef.current);
				synchronizationTimeoutRef.current = null;
			}

			const status = await loadDataCallback(offset, lastSyncedRef.current);

			if (status.skip === false) {
				lastSyncedRef.current = Date.now();
			}

			// Only set up next polling interval if shouldPoll is true and offset hasn't changed
			// Use ref to get current shouldPoll value (avoids stale closure)
			if (offset === offsetRef.current && shouldPollRef.current) {
				synchronizationTimeoutRef.current = setTimeout(
					() => {
						synchronizationTimeoutRef.current = null;

						triggerLoadRef.current?.(offset);
					},
					syncInterval,
				);
			}
		},
		[
			loadDataCallback,
		],
	);

	const hasInitialLoadRef = React.useRef(false);

	React.useEffect(
		() => {
			// Only trigger initial load if polling is enabled
			if (shouldPollRef.current) {
				hasInitialLoadRef.current = true;
				triggerLoadRef.current?.(0);
			}

			return () => {
				isMounted.current = false;

				if (loadTimeoutRef.current) {
					clearTimeout(loadTimeoutRef.current);
				}

				if (synchronizationTimeoutRef.current) {
					clearTimeout(synchronizationTimeoutRef.current);
				}
			};
		},
		[],
	);

	// Update ref when shouldPoll changes
	React.useEffect(
		() => {
			shouldPollRef.current = shouldPoll;
		},
		[
			shouldPoll,
		],
	);

	// Handle shouldPoll changes: pause/resume polling
	React.useEffect(
		() => {
			if (!shouldPoll) {
				// Pause polling when shouldPoll is false
				if (synchronizationTimeoutRef.current) {
					clearTimeout(synchronizationTimeoutRef.current);
					synchronizationTimeoutRef.current = null;
				}
			} else {
				// Resume polling when shouldPoll is true
				if (synchronizationTimeoutRef.current === null && isMounted.current) {
					// If initial load hasn't happened yet, trigger it now
					if (!hasInitialLoadRef.current) {
						hasInitialLoadRef.current = true;
						triggerLoadRef.current?.(0);
					} else {
						// Otherwise, resume polling from current offset
						triggerLoadRef.current?.(offsetRef.current);
					}
				}
			}
		},
		[
			shouldPoll,
		],
	);

	return React.useCallback(
		({ rowIndex, rowStartIndex }: { rowIndex?: number, rowStartIndex?: number }) => {
			const row = rowIndex ?? rowStartIndex ?? null;

			if (row === null) {
				return;
			}

			offsetRef.current = row;

			if (loadTimeoutRef.current) {
				clearTimeout(loadTimeoutRef.current);
			}

			offsetRef.current = row;

			loadTimeoutRef.current = setTimeout(
				() => {
					triggerLoadRef.current?.(row);
				},
				loadDataDelay,
			);
		},
		[],
	);
}



export default useDataLoading;
